import { TimePicker } from 'antd'
import { TimePickerProps } from 'antd/es/time-picker'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { ComponentController } from './component_controller'
import { DispatchEvent } from '../../types'
import { dispatcher } from '../pub_controller'

export class TimePickerController extends ComponentController<TimePickerProps> {
  static targets = ['component', 'input']

  declare componentTarget: HTMLElement
  declare inputTarget: HTMLInputElement
  declare datetimeField: string
  declare fieldId: string
  declare timeZone: string

  get component() {
    return TimePicker
  }

  get initialProps() {
    dayjs.extend(utc)
    dayjs.extend(timezone)
    const props = this.propsFromData
    const url = new URL(window.location.href)
    this.datetimeField = props['datetime_field']
    this.fieldId = props.id
    this.timeZone =
      props['time_zone'] || url.searchParams.get('time_zone') || 'UTC'
    dayjs.tz.setDefault(this.timeZone)

    props['showNow'] = false

    const datetime =
      props[this.fieldId] ||
      url.searchParams.get(this.fieldId) ||
      props['value']

    let initialValue
    if (datetime) {
      try {
        initialValue = dayjs.utc(datetime).tz(this.timeZone)
        if (!initialValue.isValid()) {
          console.warn('Invalid datetime detected:', datetime)
          initialValue = null
        }
      } catch (e) {
        console.warn('Error parsing datetime:', e, datetime)
        initialValue = null
      }
    }

    return {
      ...props,
      onChange: this.onChange.bind(this),
      value: initialValue,
    }
  }

  get rootElement() {
    return this.componentTarget
  }

  update(event: DispatchEvent<{ content: string }>) {
    const value = this.getPropsFromEvent(event)['value']
    this.updateTargets(value)
    super.update(event)
  }

  onChange(time: any, timeString: string): void {
    this.updateTargets(time)
    this.updateProps({ value: time })

    const timeInput = document.getElementById(this.fieldId) as HTMLInputElement

    if (timeInput) {
      // Parse in local timezone instead of UTC
      timeInput.value = dayjs(timeString, 'h:mm a').format('h:mm a')
    }
  }

  reset() {
    this.updateProps({ value: null })
  }

  handleTimezoneChangeEvent(event: DispatchEvent<{ time_zone: string }>): void {
    const newTZ = event.detail.time_zone
    this.timeZone = newTZ
    dayjs.tz.setDefault(newTZ)

    const hiddenField = document.getElementById(
      this.datetimeField,
    ) as HTMLInputElement
    const utcValue = hiddenField.value
    const newTime = utcValue ? dayjs.utc(utcValue).tz(newTZ) : null

    this.updateProps({ value: newTime })

    const timeInput = document.getElementById(this.fieldId) as HTMLInputElement
    if (timeInput && newTime) {
      timeInput.value = newTime.format('h:mm a')
      timeInput.dispatchEvent(new Event('change'))
    }
  }

  private updateTargets(time: Dayjs | null): void {
    const field = document.getElementById(this.fieldId) as HTMLInputElement
    field.value = time ? time.tz(this.timeZone).format('h:mm a') : ''
    field.dispatchEvent(new Event('change'))

    this.updateDateTimeField(time)

    const pubChannel = field.getAttribute('data-pub-channel-value')
    const pubAs = field.getAttribute('data-pub-as-value')

    if (pubChannel && pubAs) {
      dispatcher(this, `${pubAs}_text`, pubChannel, time)
    }
  }

  private updateDateTimeField(time: Dayjs | null): void {
    const field = document.getElementById(
      this.datetimeField,
    ) as HTMLInputElement

    if (time) {
      const currentValue = field.value
      let currentDateTime = null

      try {
        // Parse current value in the local timezone
        currentDateTime = currentValue
          ? dayjs(currentValue).tz(this.timeZone)
          : null
        if (currentDateTime && !currentDateTime.isValid()) {
          console.warn('Invalid current datetime detected:', currentValue)
          currentDateTime = null
        }
      } catch (e) {
        console.warn('Error parsing current datetime:', e, currentValue)
        currentDateTime = null
      }

      if (currentDateTime) {
        try {
          // Update the time components in the local timezone
          const newLocalDateTime = currentDateTime
            .hour(time.hour())
            .minute(time.minute())
            .second(0)
            .millisecond(0)

          // Convert to UTC for storage
          field.value = newLocalDateTime.utc().format('YYYY-MM-DD HH:mm:ss[Z]')
        } catch (e) {
          console.warn('Error updating datetime field:', e)
          // Fallback: create new datetime in local timezone then convert to UTC
          const now = dayjs().tz(this.timeZone)
          const newLocalDateTime = now
            .hour(time.hour())
            .minute(time.minute())
            .second(0)
            .millisecond(0)
          field.value = newLocalDateTime.utc().format('YYYY-MM-DD HH:mm:ss[Z]')
        }
      } else {
        // No current datetime, create new one in local timezone then convert to UTC
        const now = dayjs().tz(this.timeZone)
        const newLocalDateTime = now
          .hour(time.hour())
          .minute(time.minute())
          .second(0)
          .millisecond(0)
        field.value = newLocalDateTime.utc().format('YYYY-MM-DD HH:mm:ss[Z]')
      }
    } else {
      field.value = ''
    }

    field.dispatchEvent(new Event('change'))
  }
}
